import { useState } from "react";
import { CrmNotification, CrmLead, deleteNotification, markAllNotificationsRead, markNotificationRead } from "@/lib/crm";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Check, Trash2, UserPlus, Calendar, Flame } from "lucide-react";

interface Props {
  notifications: CrmNotification[];
  leads: CrmLead[];
  onOpenLead: (lead: CrmLead) => void;
  onRefresh: () => void;
}

const iconByType = (type: string) => {
  switch (type) {
    case "new_lead":
      return <UserPlus className="h-4 w-4" />;
    case "visit_scheduled":
      return <Calendar className="h-4 w-4" />;
    case "lead_hot":
      return <Flame className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
};

const toneByType = (type: string) => {
  switch (type) {
    case "new_lead":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "visit_scheduled":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "lead_hot":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const labelByType = (type: string) => {
  switch (type) {
    case "new_lead":
      return "Novo lead";
    case "visit_scheduled":
      return "Visita";
    case "lead_hot":
      return "Hot lead";
    case "task_overdue":
      return "Atrasado";
    case "daily_summary":
      return "Resumo";
    default:
      return type;
  }
};

const CrmNotifications = ({ notifications, leads, onOpenLead, onRefresh }: Props) => {
  const [open, setOpen] = useState(false);
  const unread = notifications.filter((n) => !n.is_read);

  const handleOpen = (n: CrmNotification) => {
    if (!n.is_read) {
      void markNotificationRead(n.id).then(onRefresh);
    }
    if (n.lead_id) {
      const lead = leads.find((l) => l.id === n.lead_id);
      if (lead) {
        setOpen(false);
        onOpenLead(lead);
      }
    }
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead();
    onRefresh();
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await deleteNotification(id);
    onRefresh();
  };

  const dateLabel = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Hoje";
    if (d.toDateString() === yesterday.toDateString()) return "Ontem";
    return d.toLocaleDateString("pt-BR");
  };

  const grouped = notifications.reduce<Record<string, CrmNotification[]>>((acc, n) => {
    const key = dateLabel(n.created_at);
    if (!acc[key]) acc[key] = [];
    acc[key].push(n);
    return acc;
  }, {});

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {unread.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
              {unread.length > 99 ? "99+" : unread.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader className="pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-display text-base flex items-center gap-2">
              Notificações
              <Badge variant="outline" className="font-body text-xs">
                {unread.length} não lida{unread.length !== 1 ? "s" : ""}
              </Badge>
            </SheetTitle>
            {unread.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAll} className="font-body text-xs">
                <Check className="h-3.5 w-3.5 mr-1" /> Marcar todas
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-100px)] mt-3">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <Bell className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="font-body text-sm text-muted-foreground">Nenhuma notificação ainda.</p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                Novos leads, visitas agendadas e leads muito quentes aparecerão aqui.
              </p>
            </div>
          ) : (
            <div className="space-y-6 pr-3">
              {Object.entries(grouped).map(([date, items]) => (
                <div key={date} className="space-y-2">
                  <p className="font-body text-[11px] uppercase tracking-wide text-muted-foreground font-semibold sticky top-0 bg-background py-1">
                    {date}
                  </p>
                  {items.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => handleOpen(n)}
                      className={`group rounded-md border p-3 cursor-pointer transition-colors hover:border-accent/50 ${
                        n.is_read ? "bg-card border-border" : "bg-accent/5 border-accent/30"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 rounded-full p-1.5 border ${toneByType(n.type)}`}>
                          {iconByType(n.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`font-body text-sm truncate ${n.is_read ? "text-muted-foreground" : "text-foreground font-semibold"}`}>
                              {n.title}
                            </p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => handleDelete(e, n.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                          </div>
                          {n.message && (
                            <p className="font-body text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {n.message}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="font-body text-[10px]">
                              {labelByType(n.type)}
                            </Badge>
                            <span className="font-body text-[10px] text-muted-foreground">
                              {new Date(n.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default CrmNotifications;
